import React, { Component } from 'react';
import { Table, Card, Button } from 'antd';
import { TenantService } from 'connection/TenantService';
import { getConfiguration, getBuilderFlow, getBotHealthPlans, validateWebhook, getOverviewInfo } from 'services/CeberusApiService';
import { BaseRequest, ConfigurationRequest, OverviewRequest } from 'models/request/Index';
import { Tenant } from 'models/Blip/Index';
import { Constants } from 'utils/constants';
import './Health.scss';
import { BuilderRequest } from 'models/request/BuilderRequest';
import { BotHealthWebhookRequest } from 'models/request/BotHealthWebhookRequest';
import { AppSettings } from '../../../appSettings'
import { BotHealthPlansRequest } from 'models/request/BotHealthPlansRequest';
import { PushNotification } from 'utils/PushNotification';
import { LoadingFactory, LOADING_TYPES } from 'components/LoadingFactory';

const { Column } = Table;

// Constant from the appSettings
let UPDATED_DESK_VERSION: string;
AppSettings.get('UPDATED_DESK_VERSION').then(value => {
  UPDATED_DESK_VERSION = value;
});
let PLAN_MAPPING: string;
AppSettings.get('PLAN_MAPPING').then(value => {
  PLAN_MAPPING = value;
});

export class BotHealth extends Component<BaseRequest> {
  state = {
    isLoading: false,
    isValidating: false,
    checkItemsTableRows: [],
    configurationTableRows: [],
    totalItems: 0,
    searchingIdentifier: '',
    flow: [],
    countDesk: 0,
    deskVersion: [],
    isDeskUpdated: false,
    botPlanMap: {} as Record<string, string>,
    botSize: '',
    isBotBuilder: false,
    hasWaba: false,
    hasValidWebhook: false,
    isPhoneNumberIdValid: false,
    validateDeskExpiration: false,
    validateDeskInvalidOut: false,
    desksWithExpiration: [],
    desksWithInvalidOutput: [],
    isTunnelOriginatorModification: false,
    tunnelOriginatorModifiedBlocks: []
  };
  tenantSelected: Tenant | null = null;

  componentDidMount() {
    this.setState({ isLoading: true });
    this.tenantSelected = TenantService.getSelectedTenant();
    this.checkBotHealth();
  }

  async loadConfigurations() {
    const identifier = this.props.BotIdentifier;
    const request = new ConfigurationRequest();
    request.BotIdentifier = identifier;
    request.ClusterName =
      (this.tenantSelected && this.tenantSelected.name) || '';

    const response = await getConfiguration(request);

    const items = response.map((item, index) => ({
      owner: `${item.owner.Name}@${item.owner.Domain}`,
      caller: `${item.caller.Name}@${item.caller.Domain}`,
      result: item.value,
      name: item.name,
      key: index
    }))

    const getBotType = items.find((config: { name: string; }) => config.name === Constants.TEMPLATE_CONFIG)?.result;
    const getWabaId = items.find((config: { name: string; }) => config.name === Constants.WABA_CONFIG)?.result;
    this.state.hasWaba = getWabaId != undefined;

    if (getWabaId) {

      const applicationRequest = new OverviewRequest();
      applicationRequest.BotIdentifier = identifier;
      applicationRequest.ClusterName =
        (this.tenantSelected && this.tenantSelected.name) || '';

      const application = await getOverviewInfo(applicationRequest);
      const getPhoneNumberId = items.find((config: { name: string; }) => config.name === Constants.PHONENUMBER_CONFIG)?.result;
      const getAccountExternalId = items.find((config: { name: string; }) => config.name === Constants.ACCOUNT_EXTERNAL_CONFIG)?.result;
      const clusterName = application.ClusterName == null ? this.tenantSelected?.name : application.ClusterName;

      this.state.hasValidWebhook = await this.isWebhookValid(getWabaId, clusterName, getPhoneNumberId, getAccountExternalId);
    }

    if (getBotType === Constants.BOT_BUILDER) {
      this.state.isBotBuilder = true;
      await this.getBotFlow();
      this.state.botSize = this.getBotSize(this.state.flow);
    }

    const totalItems = items.length;
    this.setState({
      totalItems,
      searchingIdentifier: identifier,
      configurationTableRows: items
    });
  }

  verifyPlanConsistency(): boolean {
    const { botPlanMap } = this.state;

    if (!botPlanMap) {
      return false
    }

    const { Contrato: contract, Master: master, Builder: builder, Builders } = botPlanMap;

    const buildersPlans = (Builders && typeof Builders === 'object' && !Array.isArray(Builders))
      ? Object.values(Builders)
      : [];

    const allPlans: string[] = [master, builder, ...buildersPlans].filter(Boolean) as string[];

    if (typeof contract !== 'string') {
      PushNotification.error(Constants.CONTRACT_IS_NOT_STRING);
      return false;
    }

    const rawPlanMapping = PLAN_MAPPING;
    if (typeof rawPlanMapping !== 'string') {
      PushNotification.error(Constants.PLAN_MAPPING_NOT_AVAILABLE);
      return false
    }

    let planMapping: Record<string, string>;

    try {
      planMapping = JSON.parse(rawPlanMapping);
    } catch (error) {
      PushNotification.error(Constants.PLAN_MAPPING_PARSE_FAIL);
      console.error(Constants.PLAN_MAPPING_PARSE_FAIL, error);
      return false;
    }
    const matchedKey = Object.keys(planMapping).find(
      key => key.trim().toLowerCase() === contract.trim().toLowerCase()
    );
    if (!matchedKey) {
      PushNotification.error(Constants.PLAN_MAPPING_CONTRACT_NOT_FOUND(contract));
      return false;
    }
    const normalizedContract = planMapping[matchedKey].toLowerCase();

    return allPlans.every(plan => plan.toLowerCase() === normalizedContract);
  }

  async getAllPlan() {
    try {
      const { BotIdentifier } = this.props;
      const { tenantSelected } = this;

      const request = new BotHealthPlansRequest();
      request.BotIdentifier = BotIdentifier;
      request.ClusterName = tenantSelected?.name || '';

      const response = await getBotHealthPlans(request);

      this.setState({
        botPlanMap: response
      });

    } catch (error) {
      PushNotification.error(Constants.ERROR_COLLECTING_PLANS)
      console.error(Constants.ERROR_COLLECTING_PLANS, error);
    }
  }

  async getBotFlow() {
    const identifier = this.props.BotIdentifier;
    const flow = await getBuilderFlow(
      new BuilderRequest(
        Constants.BUILDER_PUBLISHED_FLOW_BUCKET_KEY,
        this.tenantSelected.name,
        identifier,
        null
      )
    );
    const countDesk = this.countDeskFlow(flow);
    const deskVersion = this.getDeskVersion(flow);
    const isDeskUpdated = this.isDeskFlowUpdated(deskVersion);
    const validateDeskExpiration = this.validateDeskExpiration(flow);
    const validateDeskInvalidOut = this.validateDeskInvalidOut(flow);
    const isTunnelOriginatorModification = this.checkForTunnelOriginatorModification(flow);

    this.setState({ flow, countDesk, deskVersion, isDeskUpdated, validateDeskExpiration, validateDeskInvalidOut, isTunnelOriginatorModification });
  }

  async isWebhookValid(WabaId: string, ClusterName: string, PhoneNumberId: string, AccountExternalId: string): Promise<boolean> {
    const webhookRequest = new BotHealthWebhookRequest();
    webhookRequest.WabaId = WabaId;
    webhookRequest.ClusterName = ClusterName;

    let isWebhookValid = false;
    try {
      isWebhookValid = await validateWebhook(webhookRequest);
    } catch (error) {
      PushNotification.error(Constants.ERROR_VALIDATING_WEBHOOK);
    }

    const isPhoneNumberIdValid = PhoneNumberId === AccountExternalId;
    this.state.isPhoneNumberIdValid = isPhoneNumberIdValid;

    return isWebhookValid && isPhoneNumberIdValid;
  }

  getBotSize(flowJson: any): string {
    const flow = JSON.stringify(flowJson);
    const bytes = new TextEncoder().encode(flow).length;
    const kilobytes = bytes / 1024;
    const megabytes = kilobytes / 1024;

    if (megabytes >= 1) {
      return megabytes.toFixed(2) + ' MB';
    } else if (kilobytes >= 1) {
      return kilobytes.toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  }

  countDeskFlow(flow: { [key: string]: { deskStateVersion?: string } }) {
    const deskStates = Object.values(flow).filter(state =>
      state.deskStateVersion);

    return deskStates.length;
  }

  getDeskVersion(flow: { [key: string]: { deskStateVersion?: string } }) {
    const deskStates = Object.values(flow).filter(state =>
      state.deskStateVersion &&
      /\d+\.\d+\.\d+/.test(state.deskStateVersion)
    );

    const deskVersions = deskStates.map(state => state.deskStateVersion);
    return Array.from(new Set(deskVersions));
  }

  validateDeskInvalidOut(flow: any): boolean {

    const deskEntry = Object.entries(flow).filter(
      ([key,]) => key.startsWith(Constants.DESK_BLOCK_PREFIX)
    );

    if (deskEntry.length === 0) {
      return false
    }

    const desksWithInvalidOutput = deskEntry.filter(([, stateRaw]) => {
      const state = stateRaw as Record<string, any>;
      const invalidOutputs = state?.$conditionOutputs?.some((output: any) =>
        output.conditions.some(cond => cond.comparison === "exists" && cond.source === "input"

        )
      );

      return invalidOutputs;
    }).map(([key]) => key);

    this.setState({ desksWithInvalidOutput });

    return desksWithInvalidOutput.length > 0;
  }

  validateDeskExpiration(flow: any): boolean {

    const deskEntry = Object.entries(flow).filter(([key,]) => key.startsWith(Constants.DESK_BLOCK_PREFIX));

    if (deskEntry.length === 0) {
      return false;
    }

    const desksWithExpiration = deskEntry
      .filter(([, stateRaw]) => {
        const state = stateRaw as Record<string, any>;
        const expiration = state?.$contentActions?.[0]?.input?.expiration;
        return !!expiration;
      })
      .map(([key]) => key);

    this.setState({ desksWithExpiration });

    const hasExpiration = desksWithExpiration.length > 0;

    return hasExpiration;
  }

  isDeskFlowUpdated(deskVersion: string[]): boolean {
    if (deskVersion.length === 0) {
      return false;
    }

    return deskVersion.every(version => version === UPDATED_DESK_VERSION);
  }

  cleanCheckItemsTableRows() {
    this.setState({ checkItemsTableRows: [] });
  }

  hasDeskFlow() {
    return this.state.countDesk > 0;
  }

  hasDistributionTypeRedis() {
    return this.state.configurationTableRows.some(
      item =>
        item.name.toLowerCase() === Constants.DISTRIBUTION_TYPE.toLowerCase() &&
        item.result.toLowerCase() ===
        Constants.DISTRIBUTION_TYPE_REDIS.toLowerCase()
    );
  }

  hasConfigurationApplication() {
    return this.state.configurationTableRows.some(
      item =>
        item.name.toLowerCase() ===
        Constants.CONFIGURATION_APPLICATION.toLowerCase() &&
        item.owner.substring(0, 12).toLowerCase() !==
        Constants.CONFIGURATION_APPLICATION_OWNER_CONSTRUCTION.toLowerCase()
    );
  }

  checkForTunnelOriginatorModification(flow: any): boolean {
    const issues: Array<{ blockId: string; blockName: string; actionType: string; actionIndex: number }> = [];

    for (const [blockId, block] of Object.entries(flow)) {
      const customActions = Array.isArray((block as any).$enteringCustomActions) ? (block as any).$enteringCustomActions : [];

      for (const [idx, action] of customActions.entries()) {
        const type = String(action?.type || '').toLowerCase();
        const settingsJsonString = JSON.stringify(action?.settings || {}).toLowerCase();
        const modifiesTunnelOriginator = settingsJsonString.includes(Constants.TUNNEL_ORIGINATOR);

        const isMergeContactHit = type === Constants.MERGECONTACT && modifiesTunnelOriginator;
        const isSetOrMergeOnHttp = settingsJsonString.includes(Constants.SET) || settingsJsonString.includes(Constants.MERGE);
        const isProcessHttpHit = type === Constants.PROCESSHTTP && modifiesTunnelOriginator && isSetOrMergeOnHttp;

        const scriptSource = String(action?.settings?.source || '');

        const writesInScript =
          /(extras\.)?tunnel\.originator\s*=/i.test(scriptSource) ||
          /\bset\w*\s*\(\s*['"](extras\.)?tunnel\.originator['"]\s*,/i.test(scriptSource);

        const isExecuteScriptHit = type === Constants.EXECUTESCRIPT && writesInScript;

        if (isMergeContactHit || isProcessHttpHit || isExecuteScriptHit) {
          issues.push({ blockId, blockName: String((block as any)?.$title || ''), actionType: type, actionIndex: idx })
        }
      }
    }
    this.setState({ tunnelOriginatorModifiedBlocks: issues });
    return issues.length > 0;
  }

  renderBotPlansTable() {
    const { botPlanMap } = this.state;

    if (!botPlanMap) {
      return null;
    }

    const { Master: master, Builder: builder, Builders: builders } = botPlanMap;

    const rows = [
      master && { identity: 'Master', plan: master, key: 'master' },
      builder && { identity: 'Builder', plan: builder, key: 'builder' },
      ...(builders && typeof builders === 'object'
        ? Object.entries(builders).map(([id, plan]) => ({
          key: id,
          identity: id,
          plan: plan as string
        }))
        : [])
    ].filter(Boolean) as { identity: string, plan: string; key: string }[];

    const hasPagination = rows.length > Constants.MAX_ROWS_PER_PAGE;
    return (
      <Table
        dataSource={rows}
        pagination={hasPagination ? { pageSize: Constants.MAX_ROWS_PER_PAGE } : false}
      >
        <Table.Column title="Bot Identity" dataIndex='identity' key="identity" />
        <Table.Column title="plan" dataIndex='plan' key="plan" />
      </Table>
    )
  }

  renderWhatsAppDetails() {
    const webHookDetails = [];

    webHookDetails.push({
      key: 'webhookDetails',
      configuration: Constants.WEBHOOK_DETAILS,
      status: this.state.hasValidWebhook ? Constants.STATUS_OK : Constants.STATUS_FAILED
    })

    webHookDetails.push({
      key: 'phonuNumberIdDetails',
      configuration: Constants.PHONUMBERID_DETAILS,
      status: this.state.isPhoneNumberIdValid ? Constants.STATUS_OK : Constants.STATUS_FAILED
    })

    return (
      <Table
        dataSource={webHookDetails}
        rowKey="key"
      >
        <Table.Column title="Details" dataIndex='configuration' key="configuration" />
        <Table.Column title="Result" dataIndex='status' key="status" />
      </Table>
    )
  }
  renderKeyDeskBoxIdWithExpiration() {
    const desksKey = this.state.desksWithExpiration.map((deskBlockId, index) => ({
      key: index,
      deskBlockId,
      status: Constants.DESK_EXPIRATION
    }))

    return (
      <Table
        dataSource={desksKey}
        rowKey="key"
      >
        <Table.Column title="Desk Block ID" dataIndex='deskBlockId' key="deskBlockId" />
        <Table.Column title="Has Expiration" dataIndex='status' key="status" />
      </Table>
    )
  }

  renderKeyDeskBoxWithInvalidOutput() {
    const desksKey = this.state.desksWithInvalidOutput.map((deskBlockId, index) => ({
      key: index,
      deskBlockId,
      status: Constants.DESK_INVALID_OUTPUT
    }))

    return (
      <Table
        dataSource={desksKey}
        rowKey="key"
      >
        <Table.Column title="Desk Block ID" dataIndex='deskBlockId' key="deskBlockId" />
        <Table.Column title="Has Invalid OutPut" dataIndex='status' key="status" />
      </Table>
    )
  }
  renderTunnelOriginatorModifiedBlocks() {
    const blocksKey = this.state.tunnelOriginatorModifiedBlocks.map(({ blockId, blockName, actionType, actionIndex }, index) => ({
      blockId,
      blockName,
      actionType,
      actionIndex
    }))

    return (
      <Table
        dataSource={blocksKey}
        rowKey="key"
      >
        <Table.Column title="Block Name" dataIndex='blockName' key="blockName" />
        <Table.Column title="Block ID" dataIndex='blockId' key="blockId" />
        <Table.Column title="Action Type" dataIndex='actionType' key="actionType" />
        <Table.Column title="Action Index" dataIndex='actionIndex' key="actionIndex" />
      </Table>
    )
  }

  async checkBotHealth() {
    try {
      this.setState({ isValidating: true });
      this.cleanCheckItemsTableRows();
      await this.loadConfigurations();
      await this.getAllPlan();

      const results = [];

      if (this.hasDistributionTypeRedis()) {
        const distributionItem = this.state.configurationTableRows.find(
          item => item.name === Constants.DISTRIBUTION_TYPE
        );
        if (distributionItem) {
          results.push({
            name: distributionItem.name,
            result: distributionItem.result,
            status: (
              <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
                <span>{Constants.STATUS_OK}</span>
              </div>
            )
          })
        }
      } else {
        results.push({
          name: Constants.DISTRIBUTION_TYPE,
          result: Constants.STATUS_NOT_EXIST_LABEL,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_FAILED}>
              <span>{Constants.STATUS_FAILED}</span>
            </div>
          )
        })
      }

      if (this.hasConfigurationApplication()) {
        const applicationItems = this.state.configurationTableRows.filter(
          item =>
            item.name === Constants.CONFIGURATION_APPLICATION &&
            !item.owner
              .toLowerCase()
              .includes(
                Constants.CONFIGURATION_APPLICATION_OWNER_CONSTRUCTION.toLowerCase()
              )
        );

        if (applicationItems && applicationItems.length == 1) {
          results.push({
            name: applicationItems[0].name,
            result: Constants.CONFIGURATION_APPLICATION_OK,
            status: (
              <div title={Constants.CONFIGURATION_APPLICATION_OK}>
                <span>{Constants.STATUS_OK}</span>
              </div>
            )
          })
        } else {
          results.push({
            name: Constants.CONFIGURATION_APPLICATION,
            result:
              applicationItems.length === 0
                ? Constants.STATUS_NOT_EXIST_LABEL
                : Constants.STATUS_DUPLICATED_CONFIG,
            status: (
              <div title={Constants.CONFIGURATION_APPLICATION_FAILED}>
                <span>{Constants.STATUS_FAILED}</span>
              </div>
            )
          })
        }
      } else {
        results.push({
          name: Constants.CONFIGURATION_APPLICATION,
          result: Constants.STATUS_NOT_EXIST_LABEL,
          status: (
            <div title={Constants.CONFIGURATION_APPLICATION_FAILED}>
              <span>{Constants.STATUS_FAILED}</span>
            </div>
          )
        })
      }

      if (this.hasDeskFlow() && this.state.isDeskUpdated) {
        results.push({
          name: Constants.DESK_FLOW_UPDATED,
          result: Constants.UPDATED,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
              <span>{Constants.STATUS_OK}</span>
            </div>
          )
        })
      } else if (this.hasDeskFlow() && !this.state.isDeskUpdated) {
        results.push({
          name: Constants.DESK_FLOW_UPDATED,
          result: Constants.UPDATED,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_FAILED}>
              <span>{Constants.STATUS_FAILED}</span>
            </div>
          ),
        })
      }

      if (this.hasDeskFlow()) {
        results.push({
          name: Constants.DESK_FLOW,
          result: Constants.CONFIGURATION_APPLICATION_OK,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
              <span>{Constants.STATUS_OK}</span>
            </div>
          ),
          value: this.state.countDesk
        })
        results.push({
          name: Constants.DESK_FLOW_VERSION,
          result: Constants.CONFIGURATION_APPLICATION_OK,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
              <span>{Constants.STATUS_OK}</span>
            </div>
          ),
          value: this.state.deskVersion.join(', ')
        })
        if (this.state.validateDeskExpiration) {
          results.push({
            name: Constants.DESK_EXPIRATION,
            result: Constants.REMOVE_IT,
            status: (
              <div title={Constants.ALT_EXPIRATION_DESK_BOX}>
                <span>{Constants.STATUS_FAILED}</span>
              </div>
            )
          })
        }
        if (this.state.validateDeskInvalidOut) {
          results.push({
            name: Constants.DESK_INVALID_OUTPUT,
            result: Constants.REMOVE_IT,
            status: (
              <div title={Constants.ALT_INVALID_OUTPUT_DESK}>
                <span>{Constants.STATUS_FAILED}</span>
              </div>
            )
          })
        }
      }
      const isPlanConsistent = this.verifyPlanConsistency()
      results.push({
        name: Constants.PLAN_CONSISTENCY,
        result: (
          <span title='Contract plan associated with the bot'>
            {this.state.botPlanMap[Constants.CONTRACT]}
          </span>
        ),
        status: (
          <div title={isPlanConsistent ? 'All plans match the contract' : 'Inconsistent plans detected'}>
            <span>{isPlanConsistent ? Constants.STATUS_OK : Constants.STATUS_FAILED}</span>
          </div>
        )
      })
      if (this.state.isTunnelOriginatorModification) {
        results.push({
          name: Constants.TUNNEL_ORIGINATOR_MODIFIED,
          result: (
            <span title={Constants.ALT_TUNNEL_ORIGINATOR_MODIFIED}>
              {Constants.DETECTED_IN_FLOW}
            </span>
          ),
          status: (
            <div title={Constants.WARNING_TYPE}>
              <span>{Constants.STATUS_WARNING}</span>
            </div>
          )
        })
      }

      if (this.state.isBotBuilder === true) {
        results.push({
          name: Constants.BOT_FLOW_SIZE,
          result: Constants.CONFIGURATION_APPLICATION_OK,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
              <span>{Constants.STATUS_OK}</span>
            </div>
          ),
          value: this.state.botSize
        })
      }

      if (this.state.hasWaba && this.state.hasValidWebhook) {
        results.push({
          name: Constants.WEBHOOK_VALID,
          result: Constants.STATUS_VALID,
          status: (
            <div title={Constants.DISTRIBUITION_TYPE_HINT_OK}>
              <span title={Constants.WHATSAPP_DETAILS_DESCRIPTION_OK}>{Constants.STATUS_OK}</span>
            </div>
          ),
        })
      } else if (this.state.hasWaba && !this.state.hasValidWebhook) {
        results.push({
          name: Constants.WEBHOOK_VALID,
          result: Constants.STATUS_VALID,
          status: (
            <div title={Constants.CONFIGURATION_APPLICATION_FAILED}>
              <span title={Constants.WHATSAPP_DETAILS_DESCRIPTION_FAILED}>{Constants.STATUS_FAILED}</span>
            </div>
          )
        })
      }

      this.updateHealthCheckResults(results)
    } catch (error) {
      PushNotification.error('Error checking bot health');
    } finally {
      this.setState({ isValidating: false, isLoading: false });
    }
  }

  updateHealthCheckResults(results) {
    this.setState({
      checkItemsTableRows: results
    });
  }

  render() {
    if (this.state.isLoading) {
      return <LoadingFactory type={LOADING_TYPES.Partial} />;
    }

    return (
      <div>
        <Card title="Bot Health" loading={this.state.isValidating}>
          <div className="flex justify mt2">
            <Button
              type="primary"
              shape="round"
              size="small"
              icon="cloud-sync"
              loading={this.state.isValidating}
              onClick={() => this.checkBotHealth()}
            >
              Validate Bot Health
            </Button>
          </div>
          <Table
            rowKey="name"
            dataSource={this.state.checkItemsTableRows || []}
            loading={this.state.isValidating}
            expandedRowRender={record => {
              if (record.name === Constants.PLAN_CONSISTENCY) {
                return this.renderBotPlansTable();
              }
              if (record.name === Constants.WEBHOOK_VALID) {
                return this.renderWhatsAppDetails();
              }
              if (record.name === Constants.DESK_EXPIRATION) {
                return this.renderKeyDeskBoxIdWithExpiration();
              }
              if (record.name === Constants.DESK_INVALID_OUTPUT) {
                return this.renderKeyDeskBoxWithInvalidOutput();
              }
              if (record.name === Constants.TUNNEL_ORIGINATOR_MODIFIED) {
                return this.renderTunnelOriginatorModifiedBlocks();
              }
              return (
                <p style={{ margin: 0, wordBreak: 'break-word' }}>
                  <em>{'No extra details.'}</em>
                </p>
              )
            }}
          >
            <Column
              title="Name"
              dataIndex="name"
              key="name"
            />
            <Column
              title="Result"
              dataIndex="result"
              key="result"
              render={result => (
                <span>
                  {' '}
                  {result.length > 100 ? `${result.substring(0, 100)}...` : result}
                </span>
              )}
            />
            <Column title="Status" dataIndex="status" key="status" />
            {(this.hasDeskFlow() || this.state.isBotBuilder === true) && (
              <Column title="Value" dataIndex="value" key="value" />
            )}
          </Table>
        </Card>
      </div>
    )
  }
}
